import SEO from "../../../components/seo.jsx"
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import LandscapeImg from "../../../assets/training-landscape.png";
import Gallery1 from "../../../assets/training-1.png";
import Gallery2 from "../../../assets/training-2.png";
import Gallery3 from "../../../assets/training-3.png";
import Gallery4 from "../../../assets/training-4.png";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const PAGE = {
  meta: {
    title: "Hair Transplant Training | Clinique Internationale",
    description:
      "Join the Hair Transplant training course at Clinique Internationale — comprehensive, hands-on training by industry experts for those looking to excel in hair restoration procedures.",
  },

  hero: {
    eyebrow: "Training",
    heading: "Hair Transplant Training Course",
  },

  intro:
    "Clinique Internationale stands as a premier hair transplant clinic in Pune, providing an exceptional Hair Transplant training course. Renowned for its excellence, this course offers comprehensive training by industry experts, empowering participants to excel in hair restoration procedures.",

  /* ── COURSE DETAILS (dynamic fields from the PHP record) ── */
  course: {
    title: "Advanced FUE & FUT Hair Transplant Workshop",
    description:
      "A hands-on training programme covering donor extraction, graft handling, recipient site design, and implantation across both FUE and FUT techniques — led by Clinique Internationale's senior surgical team, with live case observation and supervised practical sessions.",
    priceLabel: "Course Fee",
    price: "80,000",
    startDate: "10 August 2026",
    endDate: "14 August 2026",
  },

  /* ── LANDSCAPE FEATURE IMAGE (mid-page) ── */
  landscape: {
    image: LandscapeImg,
    alt: "Clinique Internationale training session",
    caption: "Hands-on training at Clinique Internationale",
  },

  /* ── GALLERY ── */
  gallery: [
    { src: Gallery1, alt: "Training session photo 1" },
    { src: Gallery2, alt: "Training session photo 2" },
    { src: Gallery3, alt: "Training session photo 3" },
    { src: Gallery4, alt: "Training session photo 4" },
  ],

  /* ── YOUTUBE VIDEO (shown last) ── */
  video: {
    eyebrow: "Watch",
    heading: "Inside the Training Programme",
    youtubeId: "ycyowhiyUSo",
  },

  cta: {
    eyebrow: "Enquire Now",
    heading: "Interested in Joining This Course?",
    sub: "Reach out to Clinique Internationale for enrolment details, upcoming batches, and course prerequisites.",
    label: "Enquire on WhatsApp",
    href: "https://api.whatsapp.com/send?phone=+919082123009&text=I%27m%20interested%20in%20Hair%20Restoration",
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center justify-center gap-2.5 ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] text-center uppercase tracking-[0.05em] mb-10 ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function PuneTraining({ url = "https://pune.hairclinique.in/trainings.php" }) {
  const pageRef      = useRef(null);
  const heroEyeRef   = useRef(null);
  const heroHeadRef  = useRef(null);
  const sectionRefs  = useRef([]);
  const addSection   = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current,  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, y: 36 },
          { opacity: 1, y: 0, duration: 0.75, ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const wrap = "max-w-5xl mx-auto px-5 sm:px-8 lg:px-12";
  const sec  = "py-14 sm:py-18 md:py-20";

  return (
    <>
    <SEO
  title="Hair Transplant Training | Clinique Internationale"
  description="Join hands-on hair transplant training programs at Clinique Internationale, led by Dr. Ajinkya Patil. Learn Sapphire FUE techniques from experienced surgeons."
  url={url}
/>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

      <div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20" style={{ fontFamily: "'Raleway', sans-serif" }}>

        {/* ══════════════════════════════════════
            HERO
        ══════════════════════════════════════ */}
        <div className="relative h-[220px] sm:h-[260px] md:h-[300px] flex items-center justify-center overflow-hidden bg-[#0e0905]">
          <div className="absolute inset-0 opacity-40 pointer-events-none"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
          />
          <div className="relative z-10 text-center px-5">
            <p ref={heroEyeRef} style={cinzel} className="text-[10.5px] tracking-[0.32em] uppercase text-[#c9a84c] mb-4 flex items-center justify-center gap-3">
              <span className="w-7 h-px bg-[#c9a84c]" />{PAGE.hero.eyebrow}<span className="w-7 h-px bg-[#c9a84c]" />
            </p>
            <h1 ref={heroHeadRef} style={cinzel} className="font-black text-[1.7rem] sm:text-[2.2rem] md:text-[2.6rem] tracking-[0.04em] uppercase text-white leading-tight max-w-3xl mx-auto">
              {PAGE.hero.heading}
            </h1>
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-3xl mx-auto text-center">
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e]">{PAGE.intro}</p>
          </div>
        </div>

        {/* ══════════════════════════════════════
            COURSE DETAIL CARD
        ══════════════════════════════════════ */}
        <div className={wrap}>
          <div ref={addSection} className="relative overflow-hidden bg-[#0e0905] border border-[#c9a84c]/20 rounded-2xl p-8 sm:p-10 mb-14">
            <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/50 to-transparent" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] text-white uppercase tracking-[0.04em] leading-tight">
                {PAGE.course.title}
              </h2>
              <div style={cinzel} className="flex-shrink-0 text-center bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] text-[#1a0f02] rounded-xl px-6 py-3 font-black text-[15px] tracking-wide">
                {PAGE.course.priceLabel}: ₹{PAGE.course.price}
              </div>
            </div>
            <p className="text-[13.5px] leading-[1.85] text-white/60 mb-6">{PAGE.course.description}</p>
            <div className="flex flex-wrap gap-6">
              <div>
                <p style={cinzel} className="text-[10px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-1">Start Date</p>
                <p className="text-[14px] text-white/85 font-semibold">{PAGE.course.startDate}</p>
              </div>
              <div>
                <p style={cinzel} className="text-[10px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-1">End Date</p>
                <p className="text-[14px] text-white/85 font-semibold">{PAGE.course.endDate}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            LANDSCAPE FEATURE IMAGE (middle of page, full-bleed)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div ref={addSection} className={`relative ${sec}`}>
          <img
            src={PAGE.landscape.image}
            alt={PAGE.landscape.alt}
            className="w-full h-[280px] sm:h-[420px] md:h-[690px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e0905]/70 via-transparent to-transparent" />
          <p style={cinzel} className="absolute bottom-6 left-6 right-6 sm:left-10 sm:right-10 text-[12px] sm:text-[13px] tracking-[0.08em] uppercase text-white/90">
            {PAGE.landscape.caption}
          </p>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            GALLERY
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow>Photos</Eyebrow>
            <SectionHeading>Training Gallery</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {PAGE.gallery.map((g, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-[#c9a84c]/18 hover:-translate-y-1 transition-transform duration-300">
                  <img src={g.src} alt={g.alt} className="w-full h-32 sm:h-40 object-cover" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            YOUTUBE VIDEO (last section)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light>{PAGE.video.eyebrow}</Eyebrow>
              <SectionHeading light>{PAGE.video.heading}</SectionHeading>
              <div className="relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 shadow-[0_12px_40px_rgba(0,0,0,0.4)]" style={{ aspectRatio: "16 / 9" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${PAGE.video.youtubeId}`}
                  title={PAGE.video.heading}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow>{PAGE.cta.eyebrow}</Eyebrow>
            <h3 style={cinzel} className="font-black text-[1.2rem] sm:text-[1.5rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">{PAGE.cta.heading}</h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <a href={PAGE.cta.href} target="_blank" rel="noopener noreferrer"
              style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
              className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block">
              {PAGE.cta.label}
            </a>
          </div>
        </div>

      </div>
    </>
  );
}